import React from 'react';

import { NextButton, PreviousButton } from '@rbilabs/components-library';
import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { UpsellItem } from 'components/upsell-modal/upsell-item';
import { UpsellModalAddToCart } from 'components/upsell-modal/upsell-modal-add-to-cart';
import { useAddUpsellCartItem } from 'pages/cart/cart-item/use-add-upsell-cart-item';
import { CustomEventNames } from 'state/cdp/constants';
import { useOrderContext } from 'state/order';

import { CartItem } from '../cart-item/cart-item';
import { SectionHeading } from '../section-heading';

import { IUpsellContainer, IUpsellProps } from './types';
import { UpsellSkeleton } from './upsell.skeleton';
import { useScrollButtons } from './use-scroll-buttons';

const UpsellWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const CartItemsContainer = styled.div`
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

const ButtonContainer = styled.div`
  border-radius: 50%;
  background: white;
`;

const StartButtonContainer = styled(ButtonContainer)`
  position: absolute;
  inset-inline-start: 0;
  z-index: 1;
  transform: translateY(-50%);
`;

const EndButtonContainer = styled(ButtonContainer)`
  position: absolute;
  inset-inline-end: 0;
  z-index: 1;
  transform: translateY(-50%);
`;

const UpsellContainer = styled.div<IUpsellContainer>`
  overflow: auto;
  position: relative;
`;

export const UpsellLoadingContainer = styled.div`
  min-height: 100px;
  display: flex;
`;

export const Upsell: React.FC<IUpsellProps> = ({
  hasBorderAtCheckout,
  items,
  loading,
  recommender,
  recommendationToken,
  hasNewUpsellUI,
}) => {
  const {
    onScroll,
    containerRef,
    moveToTheEnd,
    moveToTheStart,
    isEndButtonShown,
    isStartButtonShown,
  } = useScrollButtons({
    items,
    offset: 16,
  });

  const { addToCart } = useOrderContext();
  const { handleAddToCart, orderAddedAlertMessage } = useAddUpsellCartItem({
    addToCart,
    items,
    recommender,
    recommendationToken,
  });

  const { formatMessage } = useIntl();

  if (!items.length) {
    return null;
  } else {
    return (
      <UpsellContainer data-testid="upsell" hasBorderAtCheckout={hasBorderAtCheckout}>
        <SectionHeading id="add-to-order-header">
          {formatMessage({ id: hasNewUpsellUI ? 'constantlyBoughtTogether' : 'addToOrderHeader' })}
        </SectionHeading>
        <UpsellWrapper>
          {isStartButtonShown && (
            <StartButtonContainer>
              <PreviousButton
                onClick={moveToTheStart}
                data-testid="tabs-scroll-left"
                aria-label={formatMessage({ id: 'previousMenuSection' })}
              />
            </StartButtonContainer>
          )}
          {loading ? (
            <UpsellLoadingContainer>
              <UpsellSkeleton hasBorderAtCheckout={hasBorderAtCheckout} />
              <UpsellSkeleton hasBorderAtCheckout={hasBorderAtCheckout} />
            </UpsellLoadingContainer>
          ) : (
            <CartItemsContainer
              aria-labelledby="add-to-order-header"
              ref={containerRef}
              onScroll={onScroll}
            >
              {items.map((item, index) => {
                if (hasNewUpsellUI) {
                  return (
                    <UpsellItem
                      flow="cart"
                      item={item}
                      index={index}
                      key={item._id}
                      actions={<UpsellModalAddToCart onClick={() => handleAddToCart(item)} />}
                    />
                  );
                }

                return (
                  <CartItem
                    key={item._id}
                    item={item}
                    addToCard={handleAddToCart}
                    logEventData={{
                      logEventName: CustomEventNames.UPSELL_DISPLAYED,
                    }}
                  />
                );
              })}
            </CartItemsContainer>
          )}
          {isEndButtonShown && (
            <EndButtonContainer>
              <NextButton
                onClick={moveToTheEnd}
                data-testid="tabs-scroll-right"
                aria-label={formatMessage({ id: 'nextMenuSection' })}
              />
            </EndButtonContainer>
          )}
        </UpsellWrapper>
        {orderAddedAlertMessage && (
          <VisuallyHidden role="alert">{orderAddedAlertMessage}</VisuallyHidden>
        )}
      </UpsellContainer>
    );
  }
};
